<template>
  <FlashNotifications ref="flash" />

  <!-- Modal -->
  <div v-show="useSettingsStoreObj().profile_preview"
       class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5">
    <div class="relative flex flex-col w-full max-w-[1520px] h-[94vh] bg-white dark:bg-[#0d1117] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.22)] border border-gray-100 dark:border-gray-800 overflow-hidden">

      <!-- ── Header ── -->
      <div class="flex-shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#0d1117]">

        <!-- Left: identity -->
        <div class="flex items-center gap-3 w-48">
          <div class="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <svg class="w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.1 0 2-.9 2-2 0-.52-.2-1-.5-1.36-.29-.35-.47-.82-.47-1.32 0-1.1.9-2 2-2h2.36c3.1 0 5.61-2.51 5.61-5.61C22.5 6.02 17.74 2 12 2zM6.5 11C5.67 11 5 10.33 5 9.5S5.67 8 6.5 8 8 8.67 8 9.5 7.33 11 6.5 11zm3-4C8.67 7 8 6.33 8 5.5S8.67 4 9.5 4s1.5.67 1.5 1.5S10.33 7 9.5 7zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 4 14.5 4s1.5.67 1.5 1.5S15.33 7 14.5 7zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 8 17.5 8s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
          <div>
            <p class="text-[13px] font-semibold text-gray-900 dark:text-white leading-none">Customize Theme</p>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Basic template</p>
          </div>
        </div>

        <!-- Center: preview toggle pill -->
        <div class="flex items-center bg-gray-100 dark:bg-[#1c2128] rounded-xl p-1 gap-0.5">
          <button @click="togglePage('profile')"
                  :class="profile_page_selected
                    ? 'bg-white dark:bg-[#2d333b] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
                  class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-150">
            <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            {{ I18N.website_content.account_settings.profile_preview }}
          </button>
          <button @click="togglePage('event')"
                  :class="event_page_selected
                    ? 'bg-white dark:bg-[#2d333b] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
                  class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-150">
            <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {{ I18N.website_content.account_settings.event_preview }}
          </button>
        </div>

        <!-- Right: close -->
        <div class="flex items-center justify-end w-48">
          <button @click="toggleModal"
                  class="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- ── Body ── -->
      <div class="flex flex-1 overflow-hidden">

        <!-- Preview pane -->
        <div class="flex-1 flex flex-col overflow-hidden bg-[#f5f6f8] dark:bg-[#161b22]">

          <!-- Browser chrome -->
          <div class="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-[#1c2128] border-b border-gray-100 dark:border-gray-800">
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <div class="w-3 h-3 rounded-full bg-red-400/80"></div>
              <div class="w-3 h-3 rounded-full bg-amber-400/80"></div>
              <div class="w-3 h-3 rounded-full bg-emerald-400/80"></div>
            </div>
            <div class="flex-1 flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-1.5 min-w-0">
              <svg class="w-3 h-3 text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span class="text-[11px] text-gray-400 dark:text-gray-500 truncate font-mono">
                {{ cleanUrl(profile_page_selected ? profile_embed_url : useSettingsStoreObj().event_embed_url) }}
              </span>
            </div>
          </div>

          <!-- iframes -->
          <div class="flex-1 relative overflow-hidden">

            <!-- Profile iframe -->
            <div v-show="profile_page_selected" class="absolute inset-0">
              <iframe :src="profile_embed_url" :key="iframeKeyProfile"
                      @load="previewLoading = false"
                      class="w-full h-full" style="border:none"></iframe>

              <!-- Profile loader -->
              <transition name="skeleton-fade">
                <div v-if="previewLoading"
                     class="absolute inset-0 bg-white/80 dark:bg-[#0d1117]/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-10">
                  <div class="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 border-t-blue-600 animate-spin"></div>
                  <p class="text-xs font-medium text-gray-400 dark:text-gray-500">Applying changes...</p>
                </div>
              </transition>
            </div>

            <!-- Event iframe -->
            <div v-show="event_page_selected" class="absolute inset-0">
              <div v-if="!useSettingsStoreObj().event_embed_url" class="flex flex-col items-center justify-center h-full gap-5">
                <div class="w-16 h-16 rounded-2xl bg-white dark:bg-[#1c2128] border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm">
                  <svg class="w-7 h-7 text-gray-300 dark:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div class="text-center">
                  <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">{{ I18N.website_content.account_settings.no_event_available }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Create an event to preview how it looks.</p>
                </div>
                <a href="/events/new"
                   class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm shadow-blue-600/20">
                  <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  {{ I18N.website_content.account_settings.create_new_event }}
                </a>
              </div>

              <template v-else>
                <iframe :src="useSettingsStoreObj().event_embed_url" :key="iframeKeyEvent"
                        @load="previewLoading = false"
                        class="w-full h-full" style="border:none"></iframe>

                <!-- Event loader -->
                <transition name="skeleton-fade">
                  <div v-if="previewLoading"
                       class="absolute inset-0 bg-white/80 dark:bg-[#0d1117]/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-10">
                    <div class="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 border-t-blue-600 animate-spin"></div>
                    <p class="text-xs font-medium text-gray-400 dark:text-gray-500">Applying changes...</p>
                  </div>
                </transition>
              </template>
            </div>
          </div>
        </div>

        <!-- ── Right panel: customisation ── -->
        <div class="w-[380px] flex-shrink-0 flex flex-col border-l border-gray-100 dark:border-gray-800 bg-white dark:bg-[#0d1117] overflow-hidden">

          <!-- Panel tab switcher -->
          <div class="flex-shrink-0 flex border-b border-gray-100 dark:border-gray-800">
            <button @click="toggleTab('banner')"
                    :class="banner_tab_show
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border-b-2 border-transparent'"
                    class="flex-1 flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium transition-colors">
              <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              {{ I18N.website_content.account_settings.banner }}
            </button>
            <button @click="toggleTab('css')"
                    :class="css_tab_show
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border-b-2 border-transparent'"
                    class="flex-1 flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium transition-colors">
              <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
              </svg>
              {{ I18N.website_content.account_settings.custom_css }}
            </button>
          </div>

          <!-- Scrollable content area -->
          <div class="flex-1 overflow-y-auto">

            <!-- ── Banner tab ── -->
            <div v-show="banner_tab_show" class="p-5 space-y-6">

              <!-- Custom banners -->
              <div>
                <p class="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500 mb-3">
                  {{ I18N.website_content.account_settings.personal.title }}
                </p>
                <div class="flex flex-wrap gap-2">

                  <!-- Custom gradient creator -->
                  <div class="relative">
                    <button @click="toggleCustomColorDropdown()"
                            class="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-200 dark:border-blue-800 text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor">
                        <path d="M339.3 367.1c27.3-3.9 51.9-19.4 67.2-42.9L568.2 74.1c12.6-19.5 9.4-45.3-7.6-61.2S517.7-4.4 499.1 9.6L262.4 187.2c-24 18-38.2 46.1-38.4 76.1L339.3 367.1zm-19.6 25.4l-116-104.4C143.9 290.3 96 339.6 96 400c0 3.9.2 7.8.6 11.6C98.4 429.1 86.4 448 68.8 448H64c-17.7 0-32 14.3-32 32s14.3 32 32 32H208c61.9 0 112-50.1 112-112 0-2.5-.1-5-.2-7.5z"/>
                      </svg>
                    </button>

                    <!-- Gradient dropdown -->
                    <div v-if="custom_color_dropdown" @click="toggleCustomColorDropdown()" class="fixed inset-0 z-10"></div>
                    <div v-if="custom_color_dropdown"
                         class="absolute left-0 top-12 z-20 w-64 bg-white dark:bg-[#1c2128] border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl shadow-black/10 overflow-hidden">
                      <div class="px-4 pt-4 pb-1">
                        <p class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          {{ I18N.website_content.account_settings.add_custom_color }}
                        </p>
                        <div class="flex items-center gap-4 mb-4">
                          <label class="flex flex-col items-center gap-1.5 cursor-pointer">
                            <span class="text-[11px] text-gray-500 dark:text-gray-400">{{ I18N.website_content.account_settings.light }}</span>
                            <div class="relative w-10 h-10 rounded-xl overflow-hidden ring-1 ring-gray-200 dark:ring-gray-700">
                              <input type="color" name="mincolor" v-model="temp_banner.banner_min_color_code" class="absolute -inset-2 w-[calc(100%+16px)] h-[calc(100%+16px)] cursor-pointer border-0 p-0" />
                            </div>
                          </label>
                          <label class="flex flex-col items-center gap-1.5 cursor-pointer">
                            <span class="text-[11px] text-gray-500 dark:text-gray-400">{{ I18N.website_content.account_settings.dark }}</span>
                            <div class="relative w-10 h-10 rounded-xl overflow-hidden ring-1 ring-gray-200 dark:ring-gray-700">
                              <input type="color" name="maxcolor" v-model="temp_banner.banner_max_color_code" class="absolute -inset-2 w-[calc(100%+16px)] h-[calc(100%+16px)] cursor-pointer border-0 p-0" />
                            </div>
                          </label>
                          <!-- preview swatch -->
                          <div class="flex-1 h-10 rounded-xl" :style="customGradientStyle"
                               :class="{'ring-2 ring-blue-500 ring-offset-1': temp_public_profile_custom_template.banner_style == 'custom_color'}">
                          </div>
                        </div>
                      </div>
                      <div class="px-4 pb-4">
                        <button @click="addCustomBanner"
                                class="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium rounded-xl transition-colors">
                          {{ I18N.website_content.account_settings.social_library.cta_text }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Upload image -->
                  <input type="file" class="hidden" ref="fileInput" accept="image/*" @change="selectImage">
                  <button @click="openFilePicker"
                          class="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor">
                      <path d="M448 80c8.8 0 16 7.2 16 16V415.8l-5-6.5-136-176c-4.5-5.9-11.6-9.3-19-9.3s-14.4 3.4-19 9.3L202 340.7l-30.5-42.7C167 291.7 159.8 288 152 288s-15 3.7-19.5 10.1l-80 112L48 416.3l0-.3V96c0-8.8 7.2-16 16-16H448zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm80 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z"/>
                    </svg>
                  </button>

                  <!-- Saved custom banners -->
                  <div v-for="(custom_banner, id) in useSettingsStoreObj().profile_banner_settings['custom_profile_banners']"
                       :key="id" class="relative group">
                    <div v-if="custom_banner.banner_type == 'color'"
                         :style="generateGradientStyle(custom_banner)"
                         @click="updateCustomBanner(custom_banner.id, 'custom_color')"
                         :class="{'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-[#0d1117]': temp_public_profile_custom_template.custom_profile_banner != null && temp_public_profile_custom_template.custom_profile_banner_id == custom_banner.id && temp_public_profile_custom_template.banner_style == 'custom_color'}"
                         class="w-12 h-12 rounded-xl cursor-pointer hover:scale-110 transition-transform">
                    </div>
                    <img v-else
                         :src="custom_banner.image_url"
                         @click="updateCustomBanner(custom_banner.id, 'custom_image')"
                         alt="Custom banner"
                         :class="{'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-[#0d1117]': temp_public_profile_custom_template.custom_profile_banner != null && temp_public_profile_custom_template.custom_profile_banner_id == custom_banner.id && temp_public_profile_custom_template.banner_style == 'custom_image'}"
                         class="w-12 h-12 rounded-xl cursor-pointer object-cover hover:scale-110 transition-transform">
                    <button @click="removeCustomBanner(custom_banner.id)"
                            class="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                      <svg class="w-2.5 h-2.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Color presets -->
              <div>
                <p class="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500 mb-3">
                  {{ I18N.application.holidays.color }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <!-- No banner -->
                  <div @click="removeBanner()"
                       :class="{'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-[#0d1117]': temp_public_profile_custom_template.banner_style == 'no_banner'}"
                       class="w-12 h-12 rounded-xl cursor-pointer border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-300 dark:text-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-400 transition-colors">
                    <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zM4 12c0-1.846.634-3.542 1.688-4.897l11.209 11.209A7.946 7.946 0 0 1 12 20c-4.411 0-8-3.589-8-8zm14.312 4.897L7.103 5.688A7.948 7.948 0 0 1 12 4c4.411 0 8 3.589 8 8a7.954 7.954 0 0 1-1.688 4.897z"/>
                    </svg>
                  </div>
                  <!-- Color swatches -->
                  <div v-for="(color, name) in profileBannerColorsList()" :key="name"
                       :style="generateGradientStyle(color)"
                       @click="updateBannerColor(color, name)"
                       :class="{'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-[#0d1117]': temp_public_profile_custom_template.banner_color != null && temp_public_profile_custom_template.banner_color == name && temp_public_profile_custom_template.banner_style == 'color'}"
                       class="w-12 h-12 rounded-xl cursor-pointer hover:scale-110 transition-transform">
                  </div>
                </div>
              </div>

              <!-- Image categories -->
              <div v-for="(category, index) in profileBannerImagesList()" :key="index">
                <p class="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500 mb-3">
                  {{ category.category }}
                </p>
                <div class="grid grid-cols-3 gap-2">
                  <div v-for="(picture, name) in category.pictures" :key="name" class="relative group/img">
                    <img :src="picture"
                         @click="updateBannerImage(name)"
                         alt="Banner"
                         :class="{'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-[#0d1117]': temp_public_profile_custom_template.banner_image_name != null && temp_public_profile_custom_template.banner_image_name == name && temp_public_profile_custom_template.banner_style == 'image'}"
                         class="w-full h-[50px] object-cover rounded-md cursor-pointer transition-all duration-150 group-hover/img:brightness-90">
                    <div class="absolute inset-0 rounded-md border-2 border-transparent group-hover/img:border-blue-400/50 transition-colors pointer-events-none"></div>
                  </div>
                </div>
              </div>

            </div>

            <!-- ── CSS tab ── -->
            <div v-show="css_tab_show" class="p-5 space-y-4">

              <div class="flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl">
                <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p class="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">{{ I18N.website_content.account_settings.do_not_include }}</p>
              </div>

              <!-- Profile CSS -->
              <div class="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div class="flex items-center justify-between px-3.5 py-2.5 bg-gray-50 dark:bg-[#161b22] border-b border-gray-200 dark:border-gray-800">
                  <div class="flex items-center gap-2">
                    <div class="flex gap-1">
                      <div class="w-2 h-2 rounded-full bg-red-400/70"></div>
                      <div class="w-2 h-2 rounded-full bg-amber-400/70"></div>
                      <div class="w-2 h-2 rounded-full bg-emerald-400/70"></div>
                    </div>
                    <span class="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      {{ I18N.website_content.account_settings.profile_layout }}
                    </span>
                  </div>
                  <button @click="runCssCode('false')"
                          class="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors">
                    <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    {{ I18N.website_content.account_settings.run_css }}
                  </button>
                </div>
                <div id="profile-custom-css" class="w-full h-40"></div>
              </div>

              <!-- Event CSS -->
              <div class="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div class="flex items-center gap-2 px-3.5 py-2.5 bg-gray-50 dark:bg-[#161b22] border-b border-gray-200 dark:border-gray-800">
                  <div class="flex gap-1">
                    <div class="w-2 h-2 rounded-full bg-red-400/70"></div>
                    <div class="w-2 h-2 rounded-full bg-amber-400/70"></div>
                    <div class="w-2 h-2 rounded-full bg-emerald-400/70"></div>
                  </div>
                  <span class="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {{ I18N.website_content.account_settings.event_layout }}
                  </span>
                </div>
                <div id="event-custom-css" class="w-full h-40"></div>
              </div>

            </div>
          </div>

          <!-- ── Panel footer ── -->
          <div class="flex-shrink-0 p-5 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
            <button @click="toggleModal"
                    class="flex-1 py-2.5 text-[13px] font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors">
              {{ I18N.website_content.connected_calendar.cancel }}
            </button>
            <button @click="runCssCode('true')"
                    class="flex-1 py-2.5 text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm shadow-blue-600/20">
              {{ I18N.website_content.account_settings.save_and_publish }}
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- Backdrop -->
  <div v-show="useSettingsStoreObj().profile_preview"
       class="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"></div>
</template>

<script>
import Constants from "controllers/vue_components/mixins/constants"
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import RequestMixin from "controllers/vue_components/mixins/request"
import FlashNotifications from "controllers/vue_components/flash_toast"
import axios from 'redaxios';

export default {
    name: 'ProfilePreview',
    props: [],
    components: {FlashNotifications},
    mixins: [Constants, RequestMixin],
    data() {
        return {
            profile_page_selected: true,
            event_page_selected: false,
            profile_embed_url: '',
            iframeKeyProfile: 0,
            iframeKeyEvent: 0,
            css_tab_show: false,
            banner_tab_show: true,
            temp_public_profile_custom_template: {},
            temp_banner: {
                banner_type: 'color',
                banner_min_color_code: '#93c5fd',
                banner_max_color_code: '#4386f5'
            },
            custom_color_dropdown: false,
            profileCssEditor: null,
            eventCssEditor: null,
            previewLoading: false,
        };
    },
    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },
        profileBannerColorsList() {
            return this.useSettingsStoreObj().profile_banner_settings['colors']
        },
        profileBannerImagesList() {
            return this.useSettingsStoreObj().profile_banner_settings['images']
        },
        toggleModal() {
            this.useSettingsStoreObj().profile_preview = !this.useSettingsStoreObj().profile_preview
            if(!this.useSettingsStoreObj().profile_preview){
                this.profile_embed_url = this.useSettingsStoreObj().current_user.profile_embed_url;
                this.useSettingsStoreObj().event_embed_url = this.useSettingsStoreObj().profile_banner_settings.last_event_embed_url;
            }
        },
        togglePage(page) {
            this.profile_page_selected = page === 'profile';
            this.event_page_selected = page === 'event';
        },
        toggleTab(page) {
            this.banner_tab_show = page === 'banner';
            this.css_tab_show = page === 'css';
        },
        updateBannerColor(color, name){
            this.temp_public_profile_custom_template.banner_color = name
            this.temp_public_profile_custom_template.banner_min_color_code = color.min
            this.temp_public_profile_custom_template.banner_max_color_code = color.max
            this.temp_public_profile_custom_template.banner_style = 'color'
            this.runCssCode('false')
        },
        removeBanner(){
            this.temp_public_profile_custom_template.banner_style = 'no_banner'
            this.runCssCode('false')
        },
        updateBannerImage(name){
            this.temp_public_profile_custom_template.banner_image_name = name
            this.temp_public_profile_custom_template.banner_style = 'image'
            this.runCssCode('false')
        },
        updateCustomBanner(id, banner_style){
            this.temp_public_profile_custom_template.custom_profile_banner_id = id
            this.temp_public_profile_custom_template.banner_style = banner_style
            this.runCssCode('false')
        },
        async addCustomBanner(){
            let data = { custom_profile_banner: this.temp_banner }
            let response = await this.axiosRequest('POST', '/api/v1/host/custom_profile_banners', data, false)
            if (response.js_error == false && response.response.success) {
                this.toggleCustomColorDropdown();
                this.updateCustomBanner(response.response.data.id, 'custom_color')
                this.useSettingsStoreObj().fetchProfileBannerSettings(true);
            }
        },
        async removeCustomBanner(id){
            let response = await this.axiosRequest('DELETE', `/api/v1/host/custom_profile_banners/${id}`, {}, false)
            if (response.js_error == false && response.response.success) {
                const activeBanner = this.useSettingsStoreObj().current_user.public_profile_custom_templates?.[0]?.custom_profile_banner
                if(activeBanner?.id == id){
                    this.removeBanner();
                    await this.runCssCode('true');
                }
                this.useSettingsStoreObj().fetchProfileBannerSettings(true);
            }
        },
        generateGradientStyle(color) {
            if(color.banner_max_color_code){
                return { background: `linear-gradient(135deg, ${color.banner_max_color_code}, ${color.banner_min_color_code})` };
            }
            return { background: `linear-gradient(135deg, ${color.max}, ${color.min})` };
        },
        async runCssCode(apply) {
            this.previewLoading = true
            this.temp_public_profile_custom_template.apply = apply
            this.temp_public_profile_custom_template.profile_custom_css = this.profileCssEditor ? this.profileCssEditor.getValue() : '';
            this.temp_public_profile_custom_template.event_custom_css = this.eventCssEditor ? this.eventCssEditor.getValue() : '';
            let data = { public_profile_custom_template: this.temp_public_profile_custom_template }
            let response = await this.axiosRequest('POST', '/api/v1/host/public_profile_custom_templates/basic/create_or_update', data, false)
            if (response.js_error == false && response.response.success) {
                if(apply == 'true'){
                    this.toggleModal();
                    this.$refs.flash.show('Styles Published', 'notice');
                }
                if (this.profile_embed_url.includes('run_process=true')) {
                    this.iframeKeyProfile++;
                } else {
                    this.profile_embed_url = this.profile_embed_url.includes('?')
                        ? this.profile_embed_url + '&run_process=true'
                        : this.profile_embed_url + '?run_process=true';
                }
                const eventUrl = this.useSettingsStoreObj().event_embed_url;
                if (eventUrl) {
                    if (eventUrl.includes('run_process=true')) {
                        this.iframeKeyEvent++;
                    } else {
                        this.useSettingsStoreObj().event_embed_url = eventUrl.includes('?')
                            ? eventUrl + '&run_process=true'
                            : eventUrl + '?run_process=true';
                    }
                }
            } else {
                this.previewLoading = false
            }
        },
        initCodeMirrorEditor(id, value) {
            const el = document.getElementById(id);
            if (!el || !window.CodeMirror) return null;
            try {
                const editor = window.CodeMirror(el, {
                    value: value || '',
                    mode: 'css',
                    theme: 'dracula',
                    lineNumbers: true,
                    autoCloseBrackets: true,
                    matchBrackets: true,
                    indentUnit: 2,
                    tabSize: 2,
                    lineWrapping: true,
                    extraKeys: { Tab: 'indentMore', 'Shift-Tab': 'indentLess' }
                });
                editor.setSize('100%', '160px');
                return editor;
            } catch (e) {
                console.warn('CodeMirror failed to initialize:', id, e);
                return null;
            }
        },
        initCodeMirrorEditors() {
            if (!window.CodeMirror) return;
            const tmpl = this.temp_public_profile_custom_template;
            this.profileCssEditor = this.initCodeMirrorEditor('profile-custom-css', tmpl.profile_custom_css || '');
            this.eventCssEditor   = this.initCodeMirrorEditor('event-custom-css',   tmpl.event_custom_css   || '');
        },
        openFilePicker() {
            this.$refs.fileInput.click();
        },
        async selectImage(event) {
            let new_file = event.target.files[0];
            if (!new_file) return;
            const formData = new FormData()
            formData.append('custom_profile_banner[banner_type]', 'image');
            formData.append('custom_profile_banner[image]', new_file);
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
                const response = await axios.post(`/api/v1/host/custom_profile_banners`, formData, {
                    headers: { 'X-CSRF-Token': csrfToken }
                });
                this.updateCustomBanner(response.data.data.id, 'custom_image')
                this.useSettingsStoreObj().fetchProfileBannerSettings(true);
            } catch (error) {
                console.error('Error uploading image:', error);
            }
        },
        cleanUrl(url) {
            if (!url) return 'your-profile-url';
            return url.replace(/[?&]run_process=true/, '');
        },
        toggleCustomColorDropdown() {
            this.custom_color_dropdown = !this.custom_color_dropdown
        },
        waitForCodeMirror(retries = 0) {
            if (window.CodeMirror) {
                this.initCodeMirrorEditors();
            } else if (retries < 50) {
                setTimeout(() => this.waitForCodeMirror(retries + 1), 100);
            }
        },
    },
    computed: {
        customGradientStyle(){
            if (this.temp_banner.banner_min_color_code != '' && this.temp_banner.banner_max_color_code != ''){
                return { background: `linear-gradient(135deg, ${this.temp_banner.banner_max_color_code}, ${this.temp_banner.banner_min_color_code})` };
            }
        },
    },
    mounted() {
        const CM = 'https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.16';
        if (!document.querySelector('link[data-codemirror]')) {
            [`${CM}/codemirror.min.css`, `${CM}/theme/dracula.min.css`].forEach(href => {
                const link = document.createElement('link');
                link.rel = 'stylesheet'; link.href = href; link.dataset.codemirror = 'css';
                document.head.appendChild(link);
            });
        }
        if (!document.querySelector('script[data-codemirror]')) {
            const base = document.createElement('script');
            base.src = `${CM}/codemirror.min.js`;
            base.dataset.codemirror = 'true';
            base.onload = () => {
                const mode = document.createElement('script');
                mode.src = `${CM}/mode/css/css.min.js`;
                const addons = document.createElement('script');
                addons.src = `${CM}/addon/edit/closebrackets.min.js`;
                document.body.appendChild(mode);
                document.body.appendChild(addons);
            };
            document.body.appendChild(base);
        }
        this.waitForCodeMirror();

        // Refresh profile_embed_url and template data each time the modal opens,
        // because created() runs before the store fetch completes on first load.
        this.$watch(
            () => this.useSettingsStoreObj().profile_preview,
            (isOpen) => {
                if (isOpen) {
                    this.profile_embed_url = this.useSettingsStoreObj().current_user.profile_embed_url || '';
                    const tmpl = this.useSettingsStoreObj().current_user.public_profile_custom_templates?.[0];
                    if (tmpl) {
                        this.temp_public_profile_custom_template = tmpl;
                        if (this.temp_public_profile_custom_template.profile_custom_css == null)
                            this.temp_public_profile_custom_template.profile_custom_css = '';
                        if (this.temp_public_profile_custom_template.event_custom_css == null)
                            this.temp_public_profile_custom_template.event_custom_css = '';
                        this.temp_public_profile_custom_template.apply = 'false';
                    }
                }
            }
        );
    },
    created() {
        this.profile_embed_url = this.useSettingsStoreObj().current_user.profile_embed_url || '';
        this.temp_public_profile_custom_template = this.useSettingsStoreObj().current_user.public_profile_custom_templates?.[0] || {}
        if(this.temp_public_profile_custom_template.profile_custom_css == null){
            this.temp_public_profile_custom_template.profile_custom_css = ''
        }
        if(this.temp_public_profile_custom_template.event_custom_css == null){
            this.temp_public_profile_custom_template.event_custom_css = ''
        }
        this.temp_public_profile_custom_template.apply = 'false'
    }
};
</script>

<style scoped>
.skeleton-fade-leave-active {
  transition: opacity 0.3s ease;
}
.skeleton-fade-leave-to {
  opacity: 0;
}
</style>
